import { CircleNotchIcon } from '@phosphor-icons/react';
import { formatCurrency, formatPercent } from './priceUtils';

interface DualPriceCellProps {
  /** Value from the Purchase Order */
  poValue: number | null | undefined;
  /** Value from the ERP/Oracle system */
  erpValue: number | null | undefined;
  /** Format type for display */
  format?: 'currency' | 'percent';
  /** Display label for PO value */
  poLabel?: string;
  /** Display label for ERP value */
  erpLabel?: string;
  /** Show loading spinner for ERP value */
  isLoading?: boolean;
}

/**
 * A cell component that displays two values side by side (PO and ERP).
 * Used for showing price/discount comparisons in the line items table.
 */
export function DualPriceCell({
  poValue,
  erpValue,
  format = 'currency',
  poLabel = 'PO',
  erpLabel = 'DB',
  isLoading = false,
}: DualPriceCellProps) {
  const formatValue = (value: number | null | undefined): string => {
    if (format === 'percent') {
      return formatPercent(value);
    }
    return formatCurrency(value);
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <span className="text-[0.7rem] text-muted-foreground font-medium min-w-[20px]">
          {poLabel}:
        </span>
        <span className="text-xs">{formatValue(poValue)}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[0.7rem] text-muted-foreground font-medium min-w-[20px]">
          {erpLabel}:
        </span>
        {isLoading ? (
          <CircleNotchIcon size={12} className="text-blue-500 animate-spin" />
        ) : (
          <span className="text-xs">{formatValue(erpValue)}</span>
        )}
      </div>
    </div>
  );
}
